/**
 * The primary key of the system. **Country-scoped from the start**, even though
 * only PL is served today.
 *
 * The tempting shortcut is to store a bare ten-digit NIP and strip the `PL`
 * prefix. That works exactly until the first German customer, at which point
 * the key is ambiguous, every stored row is retroactively PL-only, and the
 * migration touches the audit log — which is append-only by design. Carrying
 * the country now costs a field; carrying it later costs the history.
 */
/** The country assumed when a caller sends a bare number. */
export const DEFAULT_COUNTRY = "PL";
/** Canonical wire and storage form: `PL5252445767`. */
export function formatTaxId(id) {
    return `${id.country}${id.value}`;
}
export function taxIdEquals(a, b) {
    return a.country === b.country && a.value === b.value;
}
const validators = new Map();
/**
 * Registers a country's structural or checksum validation. A country with no
 * validator is **not** rejected — we accept it and let its provider decide,
 * because refusing to parse an identifier we simply have not implemented would
 * be a worse failure than passing it through.
 */
export function registerTaxIdValidator(country, validate) {
    validators.set(country.toUpperCase(), validate);
}
export function hasValidator(country) {
    return validators.has(country.toUpperCase());
}
/** Loose structural check for countries we have not implemented. */
function isStructurallyPlausible(value) {
    return /^[0-9A-Z]{4,14}$/.test(value);
}
/**
 * Normalises and validates. Returns null when unusable, rather than a
 * half-parsed value that would fragment the audit log later.
 */
export function parseTaxId(raw, options = {}) {
    const compact = raw.replace(/[\s.-]/g, "").toUpperCase();
    if (compact.length === 0)
        return null;
    const prefixed = /^([A-Z]{2})(.+)$/.exec(compact);
    const country = prefixed?.[1] ?? options.defaultCountry ?? DEFAULT_COUNTRY;
    const value = prefixed?.[2] ?? compact;
    const validate = validators.get(country);
    if (validate !== undefined) {
        return validate(value) ? { country, value } : null;
    }
    return isStructurallyPlausible(value) ? { country, value } : null;
}
//# sourceMappingURL=tax-id.js.map